import type { Coding, Reference } from '@medplum/fhirtypes'
import { formatClinicalUnit } from '@seen/core/display'
import { unitLabel } from '@seen/core/models/observationComponents'
import type { DerivationRule } from '@seen/core/observation-logic/derivations/rules'
import type { ScoreBand } from '@seen/core/observation-logic/derivations/scores'
import {
  formatValue,
  type ObservationValue,
} from '@seen/core/observation-logic/value'
import {
  type ResourceBindingId,
  resourceBinding,
} from '@seen/core/resourceBindingRegistry'
import type { BandSet } from '@seen/core/terminology/interpretations'
import { cn } from '@seen/ui'
import { format, parseISO } from 'date-fns'
import type { JSX, ReactNode } from 'react'
import { ExternalTextLink } from '#src/components/core/ExternalTextLink.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import type { DerivationSource } from '#src/components/rich-text-editor/ObservationNode/derived/useDerivationInputs.ts'

const ORIGIN_LABELS: Readonly<Record<DerivationSource['origin'], string>> = {
  note: 'this note',
  record: 'latest record',
  history: 'record',
  default: 'default',
  none: 'missing',
}

// A band's range as a clinician reads it: "18.5–22.9", "≤ 18.4", "≥ 27".
function bandRange(band: Pick<ScoreBand, 'min' | 'max'>): string {
  if (band.min !== undefined && band.max !== undefined) {
    return `${band.min}–${band.max}`
  }
  if (band.max !== undefined) {
    return `≤ ${band.max}`
  }
  if (band.min !== undefined) {
    return `≥ ${band.min}`
  }
  return 'any'
}

function Section({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): JSX.Element {
  return (
    <section className="space-y-1">
      <h4 className="text-xs font-medium text-foreground-secondary">{title}</h4>
      {children}
    </section>
  )
}

function Citation({
  citation,
  url,
}: {
  citation: string
  url?: string
}): JSX.Element {
  return (
    <p className="text-xs text-foreground-secondary">
      {citation}
      {url ? (
        <>
          {' '}
          <ExternalTextLink href={url}>Source</ExternalTextLink>
        </>
      ) : null}
    </p>
  )
}

function inputName(coding: Coding): string {
  return coding.display ?? coding.code ?? ''
}

function sourceDay(date: string): string {
  return format(parseISO(date), 'MMM d, yyyy')
}

// Each input of a computed value with its value and where it was captured:
// this note, a charted record (linked, with its day), or a default.
export function DerivationSourceList({
  sources,
  subject,
}: {
  sources: DerivationSource[]
  subject: Reference | undefined
}): JSX.Element {
  return (
    <ul className="space-y-0.5 text-xs" data-derivation-sources="">
      {sources.map((source) => {
        const recordId =
          (source.origin === 'record' || source.origin === 'history') &&
          source.reference?.startsWith('Observation/')
            ? source.reference.slice('Observation/'.length)
            : undefined
        const where = [
          source.date ? sourceDay(source.date) : undefined,
          ORIGIN_LABELS[source.origin],
        ]
          .filter(Boolean)
          .join(' · ')
        return (
          <li
            key={`${source.coding.system}|${source.coding.code}`}
            className="flex justify-between gap-3"
          >
            <span>{inputName(source.coding)}</span>
            <span
              className={cn(
                'shrink-0 tabular-nums',
                source.value === null &&
                  (source.optional ? 'text-muted-foreground' : 'text-amber-600')
              )}
              title={source.note}
            >
              {source.value === null ? (
                source.optional ? (
                  'not recorded'
                ) : (
                  'missing'
                )
              ) : (
                <>
                  {formatValue(source.value)} ·{' '}
                  {recordId && subject?.reference ? (
                    <TextLink
                      to={`/${subject.reference}/observations/${recordId}`}
                    >
                      {where}
                    </TextLink>
                  ) : (
                    where
                  )}
                </>
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

// How a computed value is worked out, for the clinician to check it: the
// method and formula, each input with its value and where it was read, the
// participant values the method needs, the result, and, where the code is
// banded, the cutoffs with the one that applies and where they come from.
export function DerivationExplanation({
  target,
  rule,
  sources,
  requirementValues,
  shown,
  override,
  band,
  bandSet,
  subject,
}: {
  target: string
  rule: DerivationRule | undefined
  sources: DerivationSource[]
  requirementValues: {
    binding: ResourceBindingId
    value: ObservationValue | null
  }[]
  shown: ObservationValue | null
  override: boolean
  band: ScoreBand | undefined
  bandSet: BandSet | undefined
  subject: Reference | undefined
}): JSX.Element {
  const method = rule?.method
  const defaultNote = sources.find(
    (source) => source.origin === 'default'
  )?.note
  const unit =
    shown?.kind === 'number' && shown.unit
      ? formatClinicalUnit(unitLabel(shown.unit))
      : undefined
  return (
    <div className="space-y-3 text-sm" data-testid="derivation-explanation">
      <div>
        <p className="font-medium">{target}</p>
        {method ? (
          <p className="text-xs text-foreground-secondary">
            Method: {method.display}
          </p>
        ) : null}
      </div>
      {method?.formula ? (
        <Section title="Formula">
          <p className="font-mono text-xs">{method.formula}</p>
        </Section>
      ) : null}
      <Section title="Inputs">
        <DerivationSourceList sources={sources} subject={subject} />
        {requirementValues.length > 0 ? (
          <ul className="space-y-0.5 text-xs">
            {requirementValues.map(({ binding, value }) => (
              <li key={binding} className="flex justify-between gap-3">
                <span>{resourceBinding(binding)?.label ?? binding}</span>
                <span
                  className={cn(
                    'shrink-0 tabular-nums',
                    !value && 'text-amber-600'
                  )}
                >
                  {value ? `${formatValue(value)} · chart` : 'missing'}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {defaultNote ? (
          <p className="mt-1 text-xs text-foreground-secondary">
            {defaultNote}
          </p>
        ) : null}
      </Section>
      <Section title="Result">
        <p className="text-xs tabular-nums">
          {shown?.kind === 'number'
            ? `${shown.value}${unit ? ` ${unit}` : ''}`
            : '—'}
          {band ? ` · ${band.display}` : ''}
          {override ? ' · edited by hand, not recalculated' : ''}
        </p>
      </Section>
      {bandSet ? (
        <Section title={`Cutoffs: ${bandSet.display}`}>
          <table className="w-full text-xs">
            <tbody>
              {bandSet.bands.map((candidate) => (
                <tr
                  key={`${candidate.display}|${bandRange(candidate)}`}
                  aria-current={candidate === band ? 'true' : undefined}
                  className={cn(candidate === band && 'font-medium')}
                >
                  <td className="py-0.5 pr-3 tabular-nums">
                    {bandRange(candidate)}
                    {unit ? ` ${unit}` : ''}
                  </td>
                  <td className="py-0.5">{candidate.display}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bandSet.source ? <Citation {...bandSet.source} /> : null}
        </Section>
      ) : null}
      {method?.note ? (
        <p className="text-xs text-foreground-secondary">{method.note}</p>
      ) : null}
      {method?.citation ? (
        <Section title="Method source">
          <Citation citation={method.citation} url={method.url} />
        </Section>
      ) : null}
    </div>
  )
}
